import { apiGet, apiPut } from "./http";

export type PointsRuleMode = "fixed" | "range";

export interface PointsRuleDto {
  ruleKey: string;
  name: string;
  pointsMode: PointsRuleMode;
  pointsFixed: number;
  pointsMin: number;
  pointsMax: number;
  description: string;
  isEnabled: boolean;
  sortOrder: number;
  pointsLabel?: string;
}

export const FIXED_RULE_KEYS = ["signin", "activity", "vote", "contribution", "invite"] as const;

export const RULE_KEY_LABELS: Record<string, string> = {
  signin: "每日签到",
  activity: "参与活动",
  vote: "参与投票",
  contribution: "协会贡献",
  invite: "邀请好友",
};

export function pointsRulesToReplaceBody(items: PointsRuleDto[]) {
  return {
    rules: items.map((r) => ({
      ruleKey: r.ruleKey,
      name: r.name,
      pointsMode: r.pointsMode,
      pointsFixed: r.pointsFixed,
      pointsMin: r.pointsMin,
      pointsMax: r.pointsMax,
      description: r.description ?? "",
      isEnabled: r.isEnabled,
      sortOrder: r.sortOrder,
    })),
  };
}

export async function fetchPointsRulesForTenant(tenantLegacyId: string): Promise<PointsRuleDto[]> {
  return apiGet<PointsRuleDto[]>(`/api/tenants/${encodeURIComponent(tenantLegacyId)}/points-rules`);
}

export async function putPointsRulesForTenant(
  tenantLegacyId: string,
  items: PointsRuleDto[],
): Promise<PointsRuleDto[]> {
  return apiPut<PointsRuleDto[]>(
    `/api/tenants/${encodeURIComponent(tenantLegacyId)}/points-rules`,
    pointsRulesToReplaceBody(items),
  );
}

export type ParticipationRuleKey = "activity" | "vote";

export interface ParticipationPointsBounds {
  ruleKey: ParticipationRuleKey;
  ruleName: string;
  isEnabled: boolean;
  pointsMode: PointsRuleMode;
  min: number;
  max: number;
}

const DEFAULT_PARTICIPATION_BOUNDS: Record<
  ParticipationRuleKey,
  { min: number; max: number; pointsMode: PointsRuleMode; pointsFixed: number }
> = {
  activity: { min: 30, max: 100, pointsMode: "range", pointsFixed: 0 },
  vote: { min: 5, max: 20, pointsMode: "range", pointsFixed: 0 },
};

export function findPointsRule(
  rules: PointsRuleDto[] | null | undefined,
  ruleKey: ParticipationRuleKey,
): PointsRuleDto | undefined {
  return (rules || []).find((r) => r.ruleKey === ruleKey);
}

export function resolveParticipationPointsBounds(
  rules: PointsRuleDto[] | null | undefined,
  ruleKey: ParticipationRuleKey,
): ParticipationPointsBounds {
  const row = findPointsRule(rules, ruleKey);
  const fallback = DEFAULT_PARTICIPATION_BOUNDS[ruleKey];
  const ruleName = row?.name?.trim() || RULE_KEY_LABELS[ruleKey] || ruleKey;
  const isEnabled = row?.isEnabled ?? true;
  const pointsMode = row?.pointsMode ?? fallback.pointsMode;
  if (pointsMode === "fixed") {
    const fixed = Math.max(0, row?.pointsFixed ?? fallback.pointsFixed);
    return { ruleKey, ruleName, isEnabled, pointsMode: "fixed", min: fixed, max: fixed };
  }
  const rawMin = row?.pointsMin ?? fallback.min;
  const rawMax = row?.pointsMax ?? fallback.max;
  const min = Math.max(0, Math.min(rawMin, rawMax));
  const max = Math.max(0, Math.max(rawMin, rawMax));
  return { ruleKey, ruleName, isEnabled, pointsMode: "range", min, max };
}

export function formatParticipationPointsHint(bounds: ParticipationPointsBounds): string {
  if (!bounds.isEnabled) {
    return `积分规则「${bounds.ruleName}」已停用，参与积分请填 0（不设奖励）`;
  }
  if (bounds.pointsMode === "fixed") {
    return bounds.min <= 0
      ? `积分规则「${bounds.ruleName}」为固定 ${bounds.min} 积分，填 0 表示不设奖励`
      : `积分规则「${bounds.ruleName}」为固定 ${bounds.min} 积分，不设奖励请填 0`;
  }
  if (bounds.min === bounds.max) {
    return `须在积分规则「${bounds.ruleName}」范围内：${bounds.min} 积分（填 0 表示不设奖励）`;
  }
  return `须在积分规则「${bounds.ruleName}」范围内：${bounds.min}~${bounds.max} 积分（填 0 表示不设奖励）`;
}

export function validateParticipationPoints(
  points: number,
  bounds: ParticipationPointsBounds,
): string | null {
  const pts = Math.max(0, Math.floor(Number(points) || 0));
  if (pts <= 0) return null;
  if (!bounds.isEnabled) {
    return `「${bounds.ruleName}」积分规则已停用，参与积分请设为 0`;
  }
  if (bounds.pointsMode === "fixed") {
    if (pts !== bounds.min) {
      return bounds.min <= 0
        ? `参与积分须为 0（「${bounds.ruleName}」规则为固定 ${bounds.min} 积分）`
        : `参与积分须为 0 或 ${bounds.min}（「${bounds.ruleName}」固定积分规则）`;
    }
    return null;
  }
  if (pts < bounds.min || pts > bounds.max) {
    return bounds.min === bounds.max
      ? `参与积分须在 ${bounds.min} 以内（填 0 表示不设奖励）`
      : `参与积分须在 ${bounds.min}~${bounds.max} 范围内（填 0 表示不设奖励）`;
  }
  return null;
}

export function parseParticipationPointsInput(raw: string): number {
  const n = parseInt(String(raw || "").trim(), 10);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}
